import { Description, Field, Switch as HeadlessSwitch, Label } from '@headlessui/react';
import classnames from 'classnames';
import React, { forwardRef } from 'react';

import { useThemeClasses } from '@/lib/legacy-theme/theme-utils';

export interface SwitchProps {
  label?: string;
  helperText?: string;
  error?: string;
  success?: string;
  variant?: 'default' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  labelPosition?: 'left' | 'right';
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ label, helperText, error, success, variant = 'default', size = 'md', checked = false, onChange, disabled, required, labelPosition = 'right', ...props }, ref) => {
    // Base switch classes
    const baseSwitchClasses =
      'relative inline-flex shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

    // Theme-aware variant classes
    const variantSwitchClasses = {
      default: 'bg-surface border-border focus:ring-primary data-[checked]:bg-primary',
      error: 'bg-surface border-error focus:ring-error data-[checked]:bg-error',
      success: 'bg-surface border-success focus:ring-success data-[checked]:bg-success',
    };

    // Size classes for switch
    const sizeSwitchClasses = {
      sm: 'h-5 w-9',
      md: 'h-6 w-11',
      lg: 'h-7 w-14',
    };

    // Toggle classes
    const baseToggleClasses = 'pointer-events-none inline-block rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out';

    // Size classes for toggle
    const sizeToggleClasses = {
      sm: 'h-4 w-4 data-[checked]:translate-x-4',
      md: 'h-5 w-5 data-[checked]:translate-x-5',
      lg: 'h-6 w-6 data-[checked]:translate-x-7',
    };

    // Determine the actual variant based on error/success props
    const actualVariant = error ? 'error' : success ? 'success' : variant;

    // Combine switch classes
    const switchClasses = useThemeClasses(classnames(baseSwitchClasses, variantSwitchClasses[actualVariant], sizeSwitchClasses[size]));

    // Combine toggle classes
    const toggleClasses = useThemeClasses(classnames(baseToggleClasses, sizeToggleClasses[size]));

    // Container classes
    const containerClasses = useThemeClasses('flex items-center gap-3');

    // Label classes
    const labelClasses = useThemeClasses(classnames('font-theme-medium text-theme-base text-text select-none', disabled && 'opacity-50'));

    // Helper text classes
    const helperTextClasses = useThemeClasses(classnames('mt-theme-xs text-theme-xs', error ? 'text-error' : success ? 'text-success' : 'text-text-muted'));

    const switchElement = (
      <HeadlessSwitch ref={ref} checked={checked} onChange={onChange} disabled={disabled} className={switchClasses} {...props}>
        <span aria-hidden="true" className={toggleClasses} />
      </HeadlessSwitch>
    );

    const labelElement = label && (
      <Label className={labelClasses}>
        {label}
        {required && <span className="text-error ml-1">*</span>}
      </Label>
    );

    return (
      <Field className={containerClasses}>
        {labelPosition === 'left' && labelElement}
        {switchElement}
        {labelPosition === 'right' && labelElement}

        {(helperText || error || success) && (
          <div className="w-full">
            <Description className={helperTextClasses}>{error || success || helperText}</Description>
          </div>
        )}
      </Field>
    );
  },
);

Switch.displayName = 'Switch';

export default Switch;
